import React, { FC, useState } from "react";
import classNames from "classnames";
import { PdpJsonLdOutput } from "../../../../../components/PdpPages/PdpJsonLdOutput";
import { glyph } from "../components/ui";
import { DisclosureRow } from "./Disclosure";
import { markupLine } from "./pageModel";
import type { PdpUrl } from "./pageModel";

/**
 * "Markup (JSON-LD) ▾" (handoff README:183-189).
 *
 * The row carries the eligibility line (`markupLine`: eligibilitySummary of
 * the page's first video, JsonLdEligibility.tsx:24 — so "Not eligible for
 * Google: missing …" shows closed too, not only "Verified") while the row is
 * closed. Open, the component prints the same line as its own first line
 * when there is a validation (PdpJsonLdOutput.tsx:337-342), so the row drops
 * it rather than say it twice (06 shows it once). "No JSON-LD generated yet"
 * (no validation, the neutral tone) stays: the component prints nothing then.
 * The body is the
 * modal's `PdpJsonLdOutput` AS-IS, with the modal's own props
 * (MODAL/ProductPdpUrlsPanel.tsx:686-694): the found-videos list, the three
 * tabs, the Copy refusal, Generate / Regenerate, the real meta-tags tab. The
 * handoff's redrawn tabs and code pane are not built; the component is not
 * edited.
 */

interface Props {
  row: PdpUrl;
  blockId: string;
  generating: boolean;
  generatingProduct: boolean;
  onGenerate: () => void;
  onGenerateProduct: () => void;
}

const TONE = { up: "text-up", down: "text-down", neutral: "text-text-2" } as const;

export const MarkupRow: FC<Props> = ({ row, blockId, generating, generatingProduct, onGenerate, onGenerateProduct }) => {
  const [open, setOpen] = useState(false);
  const bodyId = `${blockId}-markup`;
  const line = markupLine(row);
  // Neutral = no validation, which the open body doesn't print.
  const showLine = !!line && (!open || line.tone === "neutral");
  const hasVideos = (row.videos || []).length > 0;

  return (
    <div className="flex flex-col">
      <DisclosureRow
        label="Markup (JSON-LD)"
        open={open}
        onToggle={() => setOpen((v) => !v)}
        controls={bodyId}
        extra={
          showLine ? (
            <span className={classNames("inline-flex items-center gap-1.5", TONE[line.tone])} style={{ fontSize: 12 }}>
              <span className="inline-flex shrink-0" aria-hidden="true">
                {line.tone === "up" ? glyph("check", 13, 2) : glyph("info", 13, 2)}
              </span>
              {line.text}
            </span>
          ) : undefined
        }
      />
      {/* overflow-x-auto: the component's tab strip is an unwrapping
          inline-flex about 390px wide (PdpJsonLdOutput.tsx:386), wider than a
          page block at phone width. It scrolls inside this body instead of
          pushing the page sideways; the component itself is not edited. */}
      <div id={bodyId} hidden={!open} className="min-w-0 overflow-x-auto">
        {open && (
          <PdpJsonLdOutput
            className="pt-1 pb-3 min-w-0"
            row={row}
            // Video JSON-LD needs one of our videos (its transcript is the source).
            onGenerate={hasVideos ? onGenerate : undefined}
            generating={generating}
            onGenerateProduct={onGenerateProduct}
            generatingProduct={generatingProduct}
            noVideoHint="Put a video on this page from the video's settings to generate it."
          />
        )}
      </div>
    </div>
  );
};

